import { type FC, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { useOpenProductOfDayChestboxMutation } from '@/api/queries/contract';
import { AppRoute } from '@/common/app/routes';
import { productOfDayRewardsConfig } from '@/common/config/product-of-day-config';
import { formatDateToEu } from '@/common/lib/date';
import { transformToBalance } from '@/common/lib/transformToBalanct';
import { withLineBreaks } from '@/common/lib/withLinkeBreaks';
import { type TicketRewardType } from '@/common/types/lottery';
import { CharacterDialog } from '@/components/Tutorial/CharacterDialog';
import { StringMain } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { setFirstTicketReward } from '@/redux/reducers/lotteryReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { selectCurrentInProgressLoteryData } from '@/redux/selectors/lotterySelector';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import { getGuaranterReward, getMainReward } from './lib';
import ProductOfDaySpinWrapper from './ProductOfDaySpinWrapper';

import blurredLight from '/img/blurred-light.png';
import blueLightPng from '/img/light/blue-light.png';
import blueLightWebp from '/img/light/blue-light.webp';
import goldLightPng from '/img/light/gold-light.png';
import goldLightWebp from '/img/light/gold-light.webp';
import lightGreyPng from '/img/light/grey-light.png';
import lightGreyWebp from '/img/light/grey-light.webp';
import lightGreenPng from '/img/productOfDay/light-green.png';
import lightGreenWebp from '/img/productOfDay/light-green.webp';
import lightPurplePng from '/img/productOfDay/light-purple.png';
import lightPurpleWebp from '/img/productOfDay/light-purple.webp';

interface ProductOfDaySpinRewardModalProps {
  chestboxReward: {
    [field in Partial<TicketRewardType | 'tgem' | 'usdt' | 'dice'>]: number;
  }[];
  chestBoxOpenedAt?: string;
  productId: string;
}

const ProductOfDaySpinRewardModal: FC<ProductOfDaySpinRewardModalProps> = ({
  chestboxReward,
  chestBoxOpenedAt,
  productId,
}) => {
  const [isGuaranteRewardClaim, setIsGuaranteRewardClaim] = useState(false);
  const { t } = useTranslation();
  const { secondModal, closeModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const [mutate, { isLoading }] = useOpenProductOfDayChestboxMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const firstTicketReward = useAppSelector(
    (state) => state.lottery.firstTicketReward,
  );
  const navigate = useNavigate();
  const currentLottery = useAppSelector(selectCurrentInProgressLoteryData);

  const reward = useMemo(() => {
    const guarantedReward = getGuaranterReward(chestboxReward);
    const mainReward = getMainReward(chestboxReward);

    return isGuaranteRewardClaim ? guarantedReward : mainReward;
  }, [chestboxReward, isGuaranteRewardClaim]);

  const handleClose = (): void => {
    if (!chestBoxOpenedAt && !isGuaranteRewardClaim) {
      mutate({ id: productId })
        .unwrap()
        .then((response) => {
          const reward = response.reduce((acc, reward) => {
            return { ...acc, ...reward };
          }, {});
          const payload = transformToBalance(reward);
          dispatch(addToBalance(payload));
          setIsGuaranteRewardClaim(true);
        });
    }

    if (isGuaranteRewardClaim) {
      if (firstTicketReward) {
        setIsDialogOpen(true);
        dispatch(setFirstTicketReward(undefined));
      } else {
        closeModal();
        secondModal?.closeModal();
      }
    }
  };

  const getImageByType = (
    type?: TicketRewardType | 'tgem' | 'usdt' | 'dice',
  ): { img: string; webp: string } => {
    switch (type) {
      case 'usdt': {
        return { img: lightGreenPng, webp: lightGreenWebp };
      }
      case 'dice':
      case 'tgem': {
        return { img: lightPurplePng, webp: lightPurpleWebp };
      }
      case 'silverTicket': {
        return { img: lightGreyPng, webp: lightGreyWebp };
      }
      case 'goldTicket': {
        return { img: goldLightPng, webp: goldLightWebp };
      }
      case 'diamondTicket': {
        return { img: blueLightPng, webp: blueLightWebp };
      }
      default: {
        return { img: lightGreyPng, webp: lightGreyWebp };
      }
    }
  };

  const lightImage = getImageByType(reward?.type);
  const ticketsNames = ['silverTicket', 'goldTicket', 'diamondTicket'];
  const config =
    productOfDayRewardsConfig[
      reward?.type as keyof typeof productOfDayRewardsConfig
    ];

  return (
    reward && (
      <ProductOfDaySpinWrapper
        title={t('Congrats!')}
        subtitle={t('Your reward')}
        buttonText={t('Claim')}
        onSpin={handleClose}
        isLoading={isLoading}
        disabled={isLoading}
        isCompleted
      >
        <motion.div
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          className="flex-1 flex items-center justify-center self-stretch"
        >
          <div className="h-[19.375rem] w-full relative z-0 pt-20 flex items-center flex-col">
            <Picture
              className="[&>img]:object-contain absolute size-full top-0 z-[-2]"
              {...lightImage}
              alt="reward light"
            />
            <Picture
              className={cn(
                'absolute z-[-1] size-[17rem] animate-long-spin  top-4',
                ticketsNames.includes(reward.type)
                  ? 'mix-blend-luminosity'
                  : 'mix-blend-plus-lighter',
              )}
              img={blurredLight}
              alt="light"
            />

            <Picture
              className="size-36"
              img={config.icon.png}
              webp={config.icon.webp}
              alt="reward"
            />
            <StringMain weight="extrabold" className="!text-[2.5rem]">
              {reward.value}
            </StringMain>
            <StringMain weight="medium" className="!text-white/60">
              {config.label}
            </StringMain>
          </div>
          {currentLottery?.finishedAt && ticketsNames.includes(reward.type) && (
            <p className="absolute bottom-48">
              <StringMain weight="medium" className="!text-white/60">
                {t('Next draw')}:{' '}
              </StringMain>
              <StringMain weight="bold">
                {formatDateToEu(currentLottery?.finishedAt)}
              </StringMain>
            </p>
          )}
        </motion.div>
        {isDialogOpen && (
          <CharacterDialog
            title={t('🎉 Congrats!')}
            text={withLineBreaks(
              t(
                'The first tickets have been received. You’re automatically entered into the upcoming drawing! Participate in activities and earn more tickets to increase your chances of winning!',
              ),
            )}
            buttonText={t('View tickets')}
            onClose={() => {
              closeModal();
              secondModal?.closeModal();
              navigate(AppRoute.LOTTERY);
            }}
          />
        )}
      </ProductOfDaySpinWrapper>
    )
  );
};

export default ProductOfDaySpinRewardModal;
